import {GetAppInstallTeaserResponse_AppInstallTeaserResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useLocale, useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  GenericIconName,
  Icon,
  Image,
  InfoCircleWithDialog,
  StoreBadge,
  Text,
  UnstyledLink,
} from '@aboutyou/ui-components';
import {getAdjustedCdnUrl} from '@aboutyou/utils';
import {styled as linariaStyled} from '@linaria/react';
import React from 'react';
import sc from 'styled-components';

interface Props {
  onCancel: () => void;
  title: string;
  subTitle: string;
  teaser?: {
    $case: 'data';
    data: GetAppInstallTeaserResponse_AppInstallTeaserResponse;
  };
}

export const AppInstallTeaserDesktopView: React.FC<Props> = ({
  title,
  subTitle,
  teaser,
  onCancel,
}) => {
  const {t} = useTranslation();
  const locale = useLocale();

  if (!teaser?.data) {
    return null;
  }

  const {
    backgroundImage: rawBackgroundImage,
    backgroundColor,
    playStoreUrl,
    appStoreUrl,
    conditionText,
  } = teaser.data.appInstallTeaser;

  const backgroundImage = getAdjustedCdnUrl(rawBackgroundImage.url, {
    bg: backgroundColor,
  });

  return (
    <Container
      data-testid="appInstallTeaserWithSMSContainer"
      $backgroundColor={backgroundColor}
    >
      {conditionText && <InfoIcon conditions={conditionText} />}
      <CloseIcon
        asset={GenericIconName.Close}
        size="S"
        fill={COLORS.White}
        onClick={onCancel}
      />
      <ImageContainer>
        <Image
          src={backgroundImage}
          alt={rawBackgroundImage.description}
          breakpoints={[]}
          ratio="2:3"
          width={170}
          height={205}
        />
      </ImageContainer>
      <ContentContainer>
        <Text size="px20" weight="bold" color="White">
          {`${title}. ${subTitle}`}
        </Text>
        <SubContentContainer>
          <Text size="px12" weight="bold" color="White">
            {t('Or download the app directly:')}
          </Text>
          <UnstyledLink to={appStoreUrl} aria-label={t('Download the iOS app')}>
            <StoreBadge height={40} provider="ios" locale={locale} />
          </UnstyledLink>
          <UnstyledLink
            to={playStoreUrl}
            aria-label={t('Download the Android app')}
          >
            <StoreBadge height={40} provider="android" locale={locale} />
          </UnstyledLink>
        </SubContentContainer>
      </ContentContainer>
    </Container>
  );
};

const Container = sc.div<{$backgroundColor: string}>`
  display: flex;
  justify-content: space-around;
  align-items: center;
  position: relative;
  border-radius: 8px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  background-color: ${({$backgroundColor}) => $backgroundColor};
  width: 747px;
`;

const ImageContainer = sc.div`
  width: 100%;
  max-width: 298px;
  min-height: 280px;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  & > img {
    height: 205px;
  }
`;

const ContentContainer = sc.div`
  padding: 30px;
`;

const SubContentContainer = sc.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  margin-top: 30px;
  gap: 21px;
`;

const CloseIcon = linariaStyled(Icon)`
  position: absolute;
  right: 1rem;
  top: 1rem;
  &:hover {
    cursor: pointer;
  }
`;

const InfoIcon = sc(InfoCircleWithDialog)`
  position: absolute;
  left: 1rem;
  top: 1rem;
  &:hover {
    cursor: pointer;
  }
`;
